import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { MultimediaInput, required } from '@community_dev/pixels'
import { ApiError } from '@community_dev/requests'
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query'
import { Alert, Button, Input, Modal, Popconfirm, Skeleton, Typography, notification } from 'antd'
import noop from 'lodash/noop'
import { useState, type JSX } from 'react'
import { Field, Form } from 'react-final-form'
import styled from 'styled-components'

import { MUTED_TEXT } from '../../colors'
import { useChopperDomain } from '../hooks/useChopperDomain'
import {
  StyledMessageBody,
  StyledMessageHeader,
  StyledMessageItem,
  StyledMessageList,
} from '../Messages/components/styled'
import { SettingsCard, SettingsCardContent, SettingsPage } from '../Settings'

import { Client, deleteKeywordMessage, getKeywordMessages, postKeywordMessage, putKeywordMessage } from 'api/clients'
import { QUERIES } from 'constants/queries'
import { InlineEditMessage } from 'shared/components/InlineEditMessage'

const StyledEmpty = styled.div`
  padding: 16px 24px;
  color: ${MUTED_TEXT};
  font-size: 14px;
`

export function Keywords({ client }: { client: Client }): JSX.Element {
  const queryClient = useQueryClient()
  const chopperDomain = useChopperDomain(client.id, client.whiteLabelEnabled ?? false)
  const [isCreateKeywordMessageModalOpen, setIsCreateKeywordMessageModalOpen] = useState(false)
  const [createError, setCreateError] = useState<string | undefined>(undefined)
  const { isLoading, isError, data } = useQuery([QUERIES.KEYWORDS, { id: client.id }], () =>
    getKeywordMessages(client.id),
  )

  function showSuccess(message: any) {
    notification.success({
      message,
      placement: 'bottomRight',
      duration: 2,
    })
  }

  const { mutate: createKeywordMessage } = useMutation(postKeywordMessage, {
    onError(err?: ApiError) {
      const error = err?.errors?.[0]?.message
      if (error) {
        setCreateError(`Keyword ${error}`)
        return
      }
      setCreateError('Sorry, something went wrong. Please try again.')
    },
    onSuccess() {
      showSuccess('Keyword saved.')
      queryClient.invalidateQueries([QUERIES.KEYWORDS, { id: client.id }])
      setIsCreateKeywordMessageModalOpen(false)
      setCreateError(undefined)
    },
  })
  const { mutate: removeKeyword } = useMutation(deleteKeywordMessage, {
    onError() {
      notification.error({
        message: 'Keyword failed to delete. Please try again.',
        placement: 'bottomRight',
        duration: 2,
      })
    },
    onSuccess() {
      showSuccess('Keyword deleted.')
      queryClient.invalidateQueries([QUERIES.KEYWORDS, { id: client.id }])
    },
  })

  function handleCreateKeywordMessage(values: any) {
    setCreateError(undefined)
    return createKeywordMessage({ id: client.id, body: values })
  }

  function handleRemoveKeyword({ id }: any) {
    removeKeyword({ id: client.id, messageId: id })
  }

  return (
    <SettingsPage description="Keyword messages for this client." title="Keywords">
      <SettingsCard
        extra={
          <Button
            icon={<PlusOutlined />}
            onClick={() => setIsCreateKeywordMessageModalOpen((previous) => !previous)}
            type="primary"
          >
            New Keyword Message
          </Button>
        }
        title="Keyword messages"
      >
        {isLoading && (
          <SettingsCardContent>
            <Skeleton active loading={isLoading} paragraph={false} title={{ width: 50 }} />
          </SettingsCardContent>
        )}
        {isError && <StyledEmpty>Could not load the keyword messages.</StyledEmpty>}
        {!isLoading && !isError && data.length === 0 && <StyledEmpty>No keyword messages.</StyledEmpty>}
        {!isLoading && !isError && data.length > 0 && (
          <StyledMessageList>
            {data.map((message: any) => (
              <StyledMessageItem key={message.id}>
                <StyledMessageHeader>
                  <h4>{message.keyword}</h4>
                  <Popconfirm
                    cancelText="No"
                    okText="Yes"
                    onCancel={noop}
                    onConfirm={() => {
                      handleRemoveKeyword(message)
                    }}
                    placement="bottomLeft"
                    title="Are you sure you want to remove this keyword message?"
                  >
                    <Button danger icon={<DeleteOutlined />} size="small">
                      Remove
                    </Button>
                  </Popconfirm>
                </StyledMessageHeader>
                <StyledMessageBody>
                  <InlineEditMessage
                    chopperDomain={chopperDomain}
                    defaultValue={message.text}
                    name={message.keyword}
                    onSuccess={() => {
                      queryClient.invalidateQueries([QUERIES.KEYWORDS, { id: client.id }])
                    }}
                    prepare={(value) => ({
                      id: client.id,
                      messageId: message.id,
                      body: { text: value },
                    })}
                    updateEntity={putKeywordMessage}
                  />
                </StyledMessageBody>
              </StyledMessageItem>
            ))}
          </StyledMessageList>
        )}
      </SettingsCard>
      {isCreateKeywordMessageModalOpen && (
        <Form
          onSubmit={handleCreateKeywordMessage}
          render={({ handleSubmit, submitting, valid }) => (
            <Modal
              // the z-index must be less than 5 for the popover to show on top
              // of the modal.
              confirmLoading={submitting}
              okButtonProps={{ disabled: !valid }}
              okText="Add"
              onCancel={() => {
                setIsCreateKeywordMessageModalOpen(false)
                setCreateError(undefined)
              }}
              onOk={handleSubmit}
              open={isCreateKeywordMessageModalOpen}
              title="New Keyword Message"
              zIndex={4}
            >
              <form onSubmit={handleSubmit}>
                {createError && <Alert message={createError} showIcon type="error" />}

                <Field name="keyword" validate={required}>
                  {({ input, meta }) => (
                    <>
                      <label htmlFor="keyword">
                        Keyword
                        <Input id="keyword" type="text" {...input} />
                      </label>
                      {!meta.active && meta.touched && meta.modified && meta.error && (
                        <div>
                          <Typography.Text type="danger">Keyword {meta.error}</Typography.Text>
                        </div>
                      )}
                    </>
                  )}
                </Field>

                <Field name="text">
                  {({ input, meta }) => (
                    <>
                      <label htmlFor="text">
                        Message
                        <MultimediaInput
                          gallery={false}
                          gif={false}
                          onEmojiSelect={(_, text) => {
                            input.onChange(text)
                          }}
                          placeholder=""
                          {...input}
                        />
                      </label>
                      {!meta.active && meta.touched && meta.modified && meta.error && (
                        <div>
                          <Typography.Text type="danger">Message {meta.error}</Typography.Text>
                        </div>
                      )}
                    </>
                  )}
                </Field>
              </form>
            </Modal>
          )}
        />
      )}
    </SettingsPage>
  )
}
